import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Avatar,
  Divider,
  Spinner,
  Banner,
  Box,
  Modal,
  TextContainer,
} from '@shopify/polaris';
import {
  userService,
  type UserDetails,
  type UserStatus,
} from '../../services/user.service';

function getStatusBadge(status: UserStatus) {
  const toneMap: Record<UserStatus, 'success' | 'critical' | 'warning' | undefined> = {
    ACTIVE: 'success',
    INACTIVE: 'critical',
    PENDING_PROFILE: 'warning',
  };

  const labelMap: Record<UserStatus, string> = {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    PENDING_PROFILE: 'Pending',
  };

  return <Badge tone={toneMap[status]}>{labelMap[status]}</Badge>;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const loadUser = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const data = await userService.getById(id);
      setUserDetails(data);
    } catch (err) {
      setError('Failed to load user details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const handleToggleStatus = async () => {
    if (!userDetails) return;

    const newStatus = userDetails.user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

    try {
      setUpdating(true);
      await userService.updateStatus(userDetails.user.id, newStatus);
      setShowStatusModal(false);
      await loadUser();
    } catch (err) {
      setError('Failed to update user status');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Page title="Loading...">
        <Card>
          <Box padding="400">
            <InlineStack align="center">
              <Spinner />
            </InlineStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (!userDetails) {
    return (
      <Page title="User Not Found">
        <Card>
          <Text as="p">The requested user could not be found.</Text>
          <Button onClick={() => navigate('/users')}>Back to Users</Button>
        </Card>
      </Page>
    );
  }

  const { user, activity, recentInquiries, recentDownloads } = userDetails;

  return (
    <Page
      backAction={{ content: 'Users', onAction: () => navigate('/users') }}
      title={user.name || user.email}
      titleMetadata={getStatusBadge(user.status)}
      secondaryActions={[
        {
          content: user.status === 'ACTIVE' ? 'Deactivate User' : 'Activate User',
          destructive: user.status === 'ACTIVE',
          onAction: () => setShowStatusModal(true),
        },
      ]}
    >
      {error && (
        <Layout.Section>
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        </Layout.Section>
      )}

      <Layout>
        {/* User Profile */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="center">
                <Avatar
                  customer
                  size="xl"
                  name={user.name || user.email}
                  source={user.profilePicture || undefined}
                />
              </InlineStack>

              <BlockStack gap="200">
                <Text as="h2" variant="headingLg" alignment="center">
                  {user.name || 'No name set'}
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                  {user.email}
                </Text>
                {user.phone && (
                  <Text as="p" variant="bodyMd" alignment="center">
                    {user.phone}
                  </Text>
                )}
              </BlockStack>

              <Divider />

              <BlockStack gap="200">
                {user.companyName && (
                  <InlineStack align="space-between">
                    <Text as="p" tone="subdued">Company</Text>
                    <Text as="p">{user.companyName}</Text>
                  </InlineStack>
                )}
                {user.designation && (
                  <InlineStack align="space-between">
                    <Text as="p" tone="subdued">Designation</Text>
                    <Text as="p">{user.designation}</Text>
                  </InlineStack>
                )}
                <InlineStack align="space-between">
                  <Text as="p" tone="subdued">Auth Method</Text>
                  <Badge>{user.authMethod.replace(/_/g, ' ')}</Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="p" tone="subdued">Member Since</Text>
                  <Text as="p">{formatDate(user.createdAt)}</Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Activity & History */}
        <Layout.Section>
          {/* Activity Stats */}
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Activity</Text>

              <InlineStack gap="400">
                <Box
                  padding="400"
                  background="bg-surface-secondary"
                  borderRadius="200"
                  minWidth="120px"
                >
                  <BlockStack gap="100">
                    <Text as="p" variant="headingXl" alignment="center">
                      {activity.inquiryCount}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                      Inquiries
                    </Text>
                  </BlockStack>
                </Box>

                <Box
                  padding="400"
                  background="bg-surface-secondary"
                  borderRadius="200"
                  minWidth="120px"
                >
                  <BlockStack gap="100">
                    <Text as="p" variant="headingXl" alignment="center">
                      {activity.wishlistCount}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                      Wishlist Items
                    </Text>
                  </BlockStack>
                </Box>

                <Box
                  padding="400"
                  background="bg-surface-secondary"
                  borderRadius="200"
                  minWidth="120px"
                >
                  <BlockStack gap="100">
                    <Text as="p" variant="headingXl" alignment="center">
                      {activity.catalogueDownloads}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                      Downloads
                    </Text>
                  </BlockStack>
                </Box>
              </InlineStack>
            </BlockStack>
          </Card>

          {/* Recent Inquiries */}
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">Recent Inquiries</Text>
                {recentInquiries.length > 0 && (
                  <Button
                    variant="plain"
                    onClick={() => navigate(`/inquiries?userId=${user.id}`)}
                  >
                    View all
                  </Button>
                )}
              </InlineStack>

              {recentInquiries.length === 0 ? (
                <Text as="p" tone="subdued">No inquiries yet</Text>
              ) : (
                <BlockStack gap="200">
                  {recentInquiries.map((inquiry) => (
                    <Box
                      key={inquiry.id}
                      padding="300"
                      background="bg-surface-secondary"
                      borderRadius="200"
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                          <InlineStack gap="200">
                            <Text as="p" variant="bodyMd" fontWeight="semibold">
                              {inquiry.referenceNo}
                            </Text>
                            <Badge
                              tone={inquiry.type === 'BULK_ORDER' ? 'info' : undefined}
                            >
                              {inquiry.type === 'BULK_ORDER' ? 'Bulk' : 'Sample'}
                            </Badge>
                          </InlineStack>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {formatDate(inquiry.createdAt)}
                          </Text>
                        </BlockStack>
                        <Button
                          variant="plain"
                          onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                        >
                          View
                        </Button>
                      </InlineStack>
                    </Box>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>

          {/* Recent Downloads */}
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Recent Catalogue Downloads</Text>

              {recentDownloads.length === 0 ? (
                <Text as="p" tone="subdued">No downloads yet</Text>
              ) : (
                <BlockStack gap="200">
                  {recentDownloads.map((download) => (
                    <Box
                      key={download.id}
                      padding="300"
                      background="bg-surface-secondary"
                      borderRadius="200"
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="p" variant="bodyMd">
                          {download.categoryName}
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {formatDate(download.downloadedAt)}
                        </Text>
                      </InlineStack>
                    </Box>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Status Toggle Modal */}
      <Modal
        open={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title={user.status === 'ACTIVE' ? 'Deactivate User' : 'Activate User'}
        primaryAction={{
          content: user.status === 'ACTIVE' ? 'Deactivate' : 'Activate',
          destructive: user.status === 'ACTIVE',
          loading: updating,
          onAction: handleToggleStatus,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowStatusModal(false),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            {user.status === 'ACTIVE' ? (
              <p>
                Are you sure you want to deactivate this user? They will no longer be
                able to log in or access their account.
              </p>
            ) : (
              <p>
                Are you sure you want to activate this user? They will be able to log
                in and access their account again.
              </p>
            )}
          </TextContainer>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
